/**
 * Safe Area Utilities
 *
 * CSS and utility functions for handling device safe areas
 */

// CSS custom properties for safe areas
export const SAFE_AREA_CSS = `
  :root {
    /* Fallback values for older browsers */
    --safe-area-inset-top: 0px;
    --safe-area-inset-right: 0px;
    --safe-area-inset-bottom: 0px;
    --safe-area-inset-left: 0px;

    /* Try to get actual safe area values */
    --safe-area-inset-top: env(safe-area-inset-top, 0px);
    --safe-area-inset-right: env(safe-area-inset-right, 0px);
    --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
    --safe-area-inset-left: env(safe-area-inset-left, 0px);

    /* Android virtual navigation detection */
    --android-nav-height: 0px;
  }

  /* Full height that respects safe areas */
  .safe-area-full-height {
    height: 100vh;
    height: calc(100vh - var(--safe-area-inset-top) - var(--safe-area-inset-bottom));
  }

  /* Container that avoids virtual navigation */
  .safe-area-container {
    padding-top: var(--safe-area-inset-top);
    padding-bottom: max(var(--safe-area-inset-bottom), var(--android-nav-height));
    padding-left: var(--safe-area-inset-left);
    padding-right: var(--safe-area-inset-right);
  }

  /* Bottom navigation safe area - simplified for better reliability */
  .safe-bottom-nav {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 1000 !important;
    /* Simplified padding - let React handle the calculations */
    padding-bottom: max(env(safe-area-inset-bottom, 16px), 16px) !important;
  }

  /* Android specific handling */
  @media screen and (max-width: 768px) {
    :root {
      /* Estimate Android nav button height if not detected */
      --android-nav-height: clamp(0px, calc(100vh - 100dvh), 72px);
    }
  }

  /* PWA specific adjustments */
  @media (display-mode: standalone) {
    .safe-area-container {
      /* In standalone mode, rely more on safe-area-inset */
      padding-bottom: var(--safe-area-inset-bottom);
    }
  }
`;

// Utility functions
export const injectSafeAreaCSS = () => {
  const styleId = 'safe-area-styles';

  // Don't inject twice
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = SAFE_AREA_CSS;
  document.head.appendChild(style);
};

export const updateAndroidNavHeight = () => {
  try {
    const screenHeight = window.screen.height;
    const windowHeight = window.innerHeight;
    const heightDifference = screenHeight - windowHeight;

    // Be more conservative with Android nav detection to avoid false positives
    const hasAndroidNav = heightDifference > 40 && heightDifference < 120;

    if (hasAndroidNav) {
      // Cap the nav height to a reasonable maximum
      const navHeight = Math.min(Math.max(heightDifference, 0), 72);
      document.documentElement.style.setProperty('--android-nav-height', `${navHeight}px`);
    } else {
      // Set to 0 when no Android nav is detected
      document.documentElement.style.setProperty('--android-nav-height', '0px');
    }
  } catch (error) {
    console.warn('⚠️ Error updating Android nav height:', error);
    // Fallback to safe default
    document.documentElement.style.setProperty('--android-nav-height', '0px');
  }
};
